import React, { ReactNode, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import Space from "antd/es/space";
import cn from "classnames";
import camelCase from "lodash-es/camelCase";
import { useIntl } from "react-intl";

import {
  EntitiesSupportingBulkEdit,
  EntitiesSupportingDataView,
  EntitiesSupportingEmail,
  EntitiesSupportingGroups,
  EntitiesSupportingRoutes,
  EntityType,
  EntityTypesSupportingBulkEdit,
} from "@mapmycustomers/shared/types/entity";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Alert } from "@mapmycustomers/ui";

import { isCurrentUserReadOnly } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useModalVisibilityAnalyticEffect from "@app/util/hook/useModalVisibilityAnalyticEffect";
import { getEntityTypeAnalyticsName } from "@app/util/ui";

import DataViewButton from "../../DataView/DataViewButton";

import AddRemoveButton from "./components/AddRemoveButton";
import BulkEditButton from "./components/BulkEditButton";
import DataViewWrapper from "./components/DataViewWrapper";
import DeleteButton from "./components/DeleteButton";
import EmailButton from "./components/EmailButton";
import styles from "./SelectBar.module.scss";
import { isDeleting } from "./store";
import { deleteEntities } from "./store/actions";

interface Props {
  className?: string;
  deleteEntities: typeof deleteEntities.request;
  deleting: boolean;
  entityType: EntityTypesSupportingBulkEdit;
  extraActions?: ReactNode;
  isReadOnlyMember: boolean;
  onReloadData: () => void;
  selectedEntities: Set<EntitiesSupportingBulkEdit>;
  showAddRemoveButton?: boolean;
  showBulkEditButton?: boolean;
  showDataViewButton?: boolean;
  showDeleteButton?: boolean;
  showEmailButton?: boolean;
  visible: boolean;
}

const SelectBar: React.FC<Props> = ({
  className,
  deleteEntities,
  deleting,
  entityType,
  extraActions,
  isReadOnlyMember,
  onReloadData,
  selectedEntities,
  showAddRemoveButton = true,
  showBulkEditButton = true,
  showDataViewButton = true,
  showDeleteButton = true,
  showEmailButton = true,
  visible,
}) => {
  const intl = useIntl();
  const analyticIssuer = useAnalytics();
  const [dataViewVisible, , hideDataView, toggleDataView] = useBoolean();

  const selectionIds = useMemo(
    () => Array.from(selectedEntities).map((entity) => entity.id),
    [selectedEntities]
  );

  const [hasRestrictedEditItems, hasRestrictedDeleteItems] = useMemo(() => {
    let hasRestrictedEditItems = false;
    let hasRestrictedDeleteItems = false;
    for (const entity of selectedEntities) {
      hasRestrictedEditItems ||= entity.accessStatus?.update === false;
      hasRestrictedDeleteItems ||= entity.accessStatus?.delete === false;
      if (hasRestrictedEditItems && hasRestrictedDeleteItems) {
        break;
      }
    }
    return [hasRestrictedEditItems, hasRestrictedDeleteItems];
  }, [selectedEntities]);

  const hasRestrictedAccessItems = hasRestrictedEditItems || hasRestrictedDeleteItems;

  const handleHideBulkEditModal = useCallback(
    (updated: boolean) => {
      if (updated) {
        onReloadData();
      }
    },
    [onReloadData]
  );

  const [deleteConfirmVisible, showDeleteConfirm, hideDeleteConfirm] = useBoolean(false);
  const handleShowDeleteConfirm = useCallback(() => {
    showDeleteConfirm();
    analyticIssuer.initiated([`Delete ${getEntityTypeAnalyticsName(entityType, false, true)}`]);
  }, [showDeleteConfirm, analyticIssuer, entityType]);
  const handleHideDeleteConfirm = useCallback(() => {
    hideDeleteConfirm();
    analyticIssuer.cancelled([`Delete ${getEntityTypeAnalyticsName(entityType, false, true)}`]);
  }, [hideDeleteConfirm, analyticIssuer, entityType]);
  const handleDelete = useCallback(async () => {
    deleteEntities({
      entityIds: selectionIds,
      entityType,
      onSuccess: () => {
        hideDeleteConfirm();
        onReloadData();
      },
    });
    analyticIssuer.completed(
      [`Delete ${getEntityTypeAnalyticsName(entityType, false, true)}`, "Permanently Delete"],
      {
        entities: selectionIds,
      }
    );
  }, [analyticIssuer, deleteEntities, entityType, hideDeleteConfirm, selectionIds, onReloadData]);

  useModalVisibilityAnalyticEffect("Delete Records", deleteConfirmVisible, analyticIssuer);

  return (
    <>
      {visible ? (
        <div className={cn(styles.container, className)}>
          <div className={styles.title}>
            {intl.formatMessage(
              {
                id: "view.selectBar.title",
                defaultMessage:
                  "{count, select, 0 {no} other {{count}}} {entityType, select, accounts {{count, plural, one {company} other {companies}}} contacts {{count, plural, one {person} other {people}}} deals {{count, plural, one {deal} other {deals}}} crmActivities {{count, plural, one {activity} other {activities}}} trips {{count, plural, one {drive} other {drives}}} other {{count, plural, one {record} other {records}}}} selected",
                description: "Select entities bar title",
              },
              {
                count: selectedEntities.size,
                entityType: camelCase(entityType),
              }
            )}
          </div>
          <Space size="middle">
            {showBulkEditButton && (
              <BulkEditButton
                disabled={isReadOnlyMember}
                entityType={entityType}
                onHide={handleHideBulkEditModal}
                selectedEntities={selectedEntities}
              />
            )}
            {showAddRemoveButton && (
              <AddRemoveButton
                disabled={isReadOnlyMember}
                onReloadData={onReloadData}
                selectedGroupEntities={selectedEntities as Set<EntitiesSupportingGroups>}
                selectedRouteEntities={selectedEntities as Set<EntitiesSupportingRoutes>}
              />
            )}
            {showDataViewButton && (
              <DataViewButton entityCount={selectedEntities.size} onClick={toggleDataView} />
            )}
            {showEmailButton && entityType !== EntityType.TRIP && (
              <EmailButton
                disabled={isReadOnlyMember}
                selectedEntities={selectedEntities as Set<EntitiesSupportingEmail>}
              />
            )}
            {showDeleteButton && (
              <DeleteButton
                deleteConfirmationVisible={deleteConfirmVisible}
                deleting={deleting}
                disabled={isReadOnlyMember}
                entityType={entityType}
                onDelete={handleDelete}
                onHideConfirmation={handleHideDeleteConfirm}
                onShowConfirmation={handleShowDeleteConfirm}
                selectedEntityIds={selectionIds}
              />
            )}
            {extraActions}
          </Space>
          {dataViewVisible && (
            <DataViewWrapper
              onHide={hideDataView}
              selectedEntities={selectedEntities as Set<EntitiesSupportingDataView>}
            />
          )}

          {hasRestrictedAccessItems && (
            <div className={styles.warnings}>
              <Alert
                message={intl.formatMessage(
                  {
                    id: "view.selectBar.warnings.accessWarning",
                    defaultMessage:
                      "Please note, <b>one or more {entityType, select, trips {drive} other {record}} in this selection cannot be {hasRestrictedEditItems, select, true {{hasRestrictedDeleteItems, select, true {modified and/or deleted} other {modified}}} other {deleted}}</b> as you don’t have permission to change them.",
                    description: "Select entities bar - entity access warning",
                  },
                  {
                    b: (text) => <b>{text}</b>,
                    entityType,
                    hasRestrictedDeleteItems,
                    hasRestrictedEditItems,
                  }
                )}
                showIcon
                type="warning"
              />
            </div>
          )}
        </div>
      ) : (
        <div />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  deleting: isDeleting(state),
  isReadOnlyMember: isCurrentUserReadOnly(state),
});

const mapDispatchToProps = {
  deleteEntities: deleteEntities.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(SelectBar);
